import React, {useState} from 'react'
import {Grid, Typography} from '@mui/material'
import {Dialog, NumberDatePicker} from 'finsys-webcomponent'
import moment from 'moment'
import {useMutation} from 'react-query'

import {useQuery} from 'react-query'
import {
  exportTicketSummary,
  fetchEntitiesBasedonCountry,
} from 'api/TS/query/InternalRequestQueries'
import {useSnackBar} from 'hoc/SnackbarHandler'
import MultipleSelect from 'components/InternalRequest/Summary/FilterOptions/MultipleSelect'

type ExportDialogProps = {
  handleOnClose: () => void,
}

/**
 * A pop up dialog for Export component
 * @return {JSX.Element}
 * @constructor
 */
export const ExportDialog = ({
  tab,
  countries,
  entities,
  category,
  sub_category,
  status,
  handleOnClose,
}: ExportDialogProps) => {
  const {setSnackBar} = useSnackBar()
  const [exportCondition, setExportCondition] = useState({
    country: [],
    entity: [],
    categories: [],
    sub_categories: [],
    ticket_status: [],
    creation_date_from: moment('2021-01-01', 'YYYYMMDD'),
    creation_date_to: moment(),
    last_update_date_from: moment('2021-01-01', 'YYYYMMDD'),
    last_update_date_to: moment(),
  })

  const handleOnExportChange = (key, value) => {
    setExportCondition((prevState) => ({
      ...prevState,
      [key]: value,
    }))
  }

  /**
   * To retrieve entities based on country
   */
  // filter entities based on tab, country
  const [get_country_value, set_country_value] = useState(null)
  const {loading: isEntitiesLoading, data: entities_api} = useQuery(
    ['IRF-entities', get_country_value],
    () => fetchEntitiesBasedonCountry(get_country_value, entities),
    {refetchOnWindowFocus: true}
  )

  /**
   * To handle Export ticket
   * @type {UseMutationResult<DefaultSelectType, unknown, void, unknown>}
   */
  const exportTicket = useMutation(() => exportTicketSummary(exportCondition, tab), {
    onSuccess: () => {
      setSnackBar({
        open: true,
        type: 'success',
        message: `Exported`,
      })
    },
    onError: (err) => {
      setSnackBar({
        open: true,
        type: 'info',
        message: 'No data available to export, please try other filters',
      })
      console.log(err)
    },
    onSettled: handleOnClose,
  })

  /**
   * To validate and export ticket
   */
  const handleExportTicket = () => {
    let isExportTicketValid = true

    /**
     * Convenient function to validate date
     * @param item
     * @param field
     */
    const toValidateDate = (item, field) => {
      if (
        (exportCondition[field] && !moment(exportCondition[field]).isValid()) ||
        exportCondition[`error_${field}`]
      ) {
        isExportTicketValid = false
      }
    }

    ;[
      'creation_date_from',
      'creation_date_to',
      'last_update_date_from',
      'last_update_date_to',
    ].forEach((value) => {
      if (!exportCondition[value]) {
        isExportTicketValid = false

        setExportCondition((prevState) => ({
          ...prevState,
          [`error_${value}`]: 'This field is required',
        }))
      }

      if (
        [
          'creation_date_from',
          'creation_date_to',
          'last_update_date_from',
          'last_update_date_to',
        ].includes(value)
      ) {
        toValidateDate(exportCondition, value)
      }
    })

    if (isExportTicketValid) {
      exportTicket.mutate()
    } else {
      setSnackBar({
        open: true,
        type: 'error',
        message: 'Missing/ Invalid input! Please fill up all the required input',
      })
    }
  }

  return (
    <Dialog
      maxWidth={'lg'}
      handleOnClose={handleOnClose}
      buttons={[
        {
          label: 'Cancel',
          onClick: handleOnClose,
          type: 'text',
          disabled: exportTicket.isLoading,
        },
        {
          label: 'Export',
          onClick: handleExportTicket,
          type: 'primary',
          loading: exportTicket.isLoading,
        },
      ]}
      content={
        <Grid container justifyContent={'flex-end'} spacing={2}>
          <Grid item xs={12} container direction="row" columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                General
              </Typography>
            </Grid>
            <Grid container item direction={'row'}>
              <Grid
                container
                item
                xs={12}
                sm={12}
                md={12}
                direction={'row'}
                columnSpacing={4}
                rowSpacing={2}
              >
                <Grid item xs={12} sm={6} md={6}>
                  <MultipleSelect
                    isAll={true}
                    selections={[
                      {
                        country_id: 'ALL',
                        country_name: 'ALL',
                      },
                      ...countries?.filter((value) => {
                        return value.country_id !== '' && value.country_id !== null
                      }),
                    ]}
                    limitTags={5}
                    label={'Select Country'}
                    onChange={(event, value) => {
                      if (value[value.length - 1]?.country_id === 'ALL') {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          country:
                            exportCondition.country.length ===
                            countries?.filter(
                              (value) =>
                                value.country_id !== '' && value.country_id !== null
                            ).length
                              ? []
                              : countries?.filter(
                                  (value) =>
                                    value.country_id !== '' && value.country_id !== null
                                ),
                        }))
                        set_country_value(countries)
                      } else {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          country: value,
                        }))
                        set_country_value(value)
                      }
                    }}
                    value={exportCondition.country}
                    getOptionLabel={(option) => {
                      return `${option?.country_name ?? ''} ${
                        option?.country_name
                          ? `(${option?.country_id})`
                          : option?.country_id ?? ''
                      }`
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <MultipleSelect
                    isAll={true}
                    selections={
                      !entities_api || get_country_value === 'ALL'
                        ? [
                            {
                              entity_id: 2,
                              entity_code: 'ALL',
                              entity_name: 'ALL',
                            },
                            ...entities?.filter(
                              (value) => value.entity !== '' && value.entity !== null
                            ),
                          ]
                        : [
                            {
                              entity_id: 2,
                              entity_code: 'ALL',
                              entity_name: 'ALL',
                            },
                            ...entities_api?.filter(
                              (value) => value.entity !== '' && value.entity !== null
                            ),
                          ]
                    }
                    limitTags={5}
                    label={'Select Entity'}
                    onChange={(event, value) => {
                      if (value[value.length - 1]?.entity_code === 'ALL') {
                        let entities_ref = !entities_api ? entities : entities_api
                        setExportCondition((prevState) => ({
                          ...prevState,
                          entity:
                            exportCondition.entity.length ===
                            entities_ref?.filter(
                              (value) =>
                                value.entity_code !== '' && value.entity_code !== null
                            ).length
                              ? []
                              : entities_ref?.filter(
                                  (value) => value.entity !== '' && value.entity !== null
                                ),
                        }))
                      } else {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          entity: value,
                        }))
                      }
                    }}
                    value={exportCondition.entity}
                    getOptionLabel={(option) => {
                      return `${option?.entity_name ?? ''} ${
                        option?.entity_name
                          ? `(${option?.entity_code})`
                          : option?.entity_code ?? ''
                      }`
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.entity_id === value.entity_id
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <MultipleSelect
                    isAll={true}
                    selections={[
                      'All',
                      ...category?.filter((value) => value !== '' && value !== null),
                    ]}
                    limitTags={5}
                    label={'Select Category'}
                    onChange={(event, value) => {
                      if (value[value.length - 1] === 'All') {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          categories:
                            exportCondition.categories.length ===
                            category?.filter((value) => value !== '' && value !== null)
                              .length
                              ? []
                              : category?.filter(
                                  (value) => value !== '' && value !== null
                                ),
                        }))
                      } else {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          categories: value,
                        }))
                      }
                    }}
                    value={exportCondition.categories}
                    getOptionLabel={(option) => {
                      return option
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <MultipleSelect
                    isAll={true}
                    selections={[
                      'All',
                      ...sub_category?.filter((value) => value !== '' && value !== null),
                    ]}
                    limitTags={5}
                    label={'Select Sub Category'}
                    onChange={(event, value) => {
                      if (value[value.length - 1] === 'All') {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          sub_categories:
                            exportCondition.sub_categories.length ===
                            sub_category?.filter(
                              (value) => value !== '' && value !== null
                            ).length
                              ? []
                              : sub_category?.filter(
                                  (value) => value !== '' && value !== null
                                ),
                        }))
                      } else {
                        setExportCondition((prevState) => ({
                          ...prevState,
                          sub_categories: value,
                        }))
                      }
                    }}
                    value={exportCondition.sub_categories}
                    getOptionLabel={(option) => {
                      return option
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} container direction="row" columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Status
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <MultipleSelect
                isAll={true}
                selections={[
                  'All',
                  ...status?.filter((value) => value !== '' && value !== null),
                ]}
                limitTags={5}
                label={'Select Ticket Status'}
                onChange={(event, value) => {
                  if (value[value.length - 1] === 'All') {
                    setExportCondition((prevState) => ({
                      ...prevState,
                      ticket_status:
                        exportCondition.ticket_status.length ===
                        status?.filter((value) => value !== '' && value !== null).length
                          ? []
                          : status?.filter((value) => value !== '' && value !== null),
                    }))
                  } else {
                    setExportCondition((prevState) => ({
                      ...prevState,
                      ticket_status: value === 'None' ? '' : value,
                    }))
                  }
                }}
                value={exportCondition.ticket_status}
                getOptionLabel={(option) => {
                  return option
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} container direction="row" columnSpacing={4} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Dates
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <NumberDatePicker
                setSelectedDate={(value) => {
                  setExportCondition((prevState) => ({
                    ...prevState,
                    creation_date_from: value ? moment(value).format('yyyy-MM-DD') : null,
                    error_creation_date_from: null,
                  }))
                }}
                value={exportCondition.creation_date_from ?? null}
                label={'Select Created On From'}
                maxDate={moment(exportCondition.creation_date_to)}
                required
                error={!!exportCondition.error_creation_date_from}
                helperText={exportCondition.error_creation_date_from}
                handleOnError={(error) => {
                  handleOnExportChange('error_creation_date_from', error)
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <NumberDatePicker
                setSelectedDate={(value) => {
                  setExportCondition((prevState) => ({
                    ...prevState,
                    creation_date_to: value ? moment(value).format('yyyy-MM-DD') : null,
                    error_creation_date_to: null,
                  }))
                }}
                value={exportCondition.creation_date_to ?? null}
                minDate={moment(exportCondition.creation_date_from)}
                maxDate={moment(new Date())}
                label={'Select Created On To'}
                required
                error={!!exportCondition.error_creation_date_to}
                helperText={exportCondition.error_creation_date_to}
                handleOnError={(error) => {
                  handleOnExportChange('error_creation_date_to', error)
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <NumberDatePicker
                setSelectedDate={(value) => {
                  setExportCondition((prevState) => ({
                    ...prevState,
                    last_update_date_from: value
                      ? moment(value).format('yyyy-MM-DD')
                      : null,
                    error_last_update_date_from: null,
                  }))
                }}
                maxDate={moment(exportCondition.last_update_date_to)}
                value={exportCondition.last_update_date_from ?? null}
                label={'Select Last Updated on From'}
                required
                error={!!exportCondition.error_last_update_date_from}
                helperText={exportCondition.error_last_update_date_from}
                handleOnError={(error) => {
                  handleOnExportChange('error_last_update_date_from', error)
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <NumberDatePicker
                setSelectedDate={(value) => {
                  setExportCondition((prevState) => ({
                    ...prevState,
                    last_update_date_to: value
                      ? moment(value).format('yyyy-MM-DD')
                      : null,
                    error_last_update_date_to: null,
                  }))
                }}
                minDate={moment(exportCondition.last_update_date_from)}
                maxDate={moment(new Date())}
                value={exportCondition.last_update_date_to ?? null}
                label={'Select Last Updated on To'}
                required
                error={!!exportCondition.error_last_update_date_to}
                helperText={exportCondition.error_last_update_date_to}
                handleOnError={(error) => {
                  handleOnExportChange('error_last_update_date_to', error)
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      }
      disableBackdropClick
      title="Export Ticket"
      type="info"
    />
  )
}
